import { constants } from '@config';
import { DataUrlService } from 'src/services/data-url';
import { FontService } from 'src/services/font';
import { logError } from 'src/utils/logger';

import { PageContentFactory } from '../page-content';

import { imageFallback } from './page-to-image.config';
import {
  CANVAS_DIMENSION_LIMIT,
  CSS_URL_REGEX,
  DEFAULT_OPTIONS,
  SELECTOR_BACKGROUND_COLOR,
  SELECTOR_BACKGROUND_IMAGE
} from './page-to-image.constants';
import { isIOSDevice, isSafari } from 'src/utils/safari';
const { NS } = constants;

export class PageToImageFactory {
  log = logError('PageToImageFactory');
  canvas = document.createElement('canvas');
  dataUrlService = new DataUrlService();
  fontService = new FontService();
  pageContentFactory = new PageContentFactory();

  async getImage (pageId, givenOptions) {
    const options = { ...DEFAULT_OPTIONS, ...givenOptions };

    const svgElement = await this.pageContentFactory.getSvgElement(pageId);

    const imageUrl = await this.svgElementToImageDataUrl(svgElement, options);

    return imageUrl;
  }

  async getImageDataUrlFromContent (content, givenOptions) {
    const options = { ...DEFAULT_OPTIONS, ...givenOptions };
    const svgElement = this.pageContentFactory.createSvgElement(content);
    const imageUrl = await this.svgElementToImageDataUrl(svgElement, options);
    return imageUrl;
  }

  async createImageElement (url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.addEventListener('load', () => {
        URL.revokeObjectURL(url);
        resolve(image);
      }, {
        once: true
      });
      image.addEventListener('error', (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      }, {
        once: true
      });
      image.src = url;
    });
  }

  getSvgElementSize (svgElement, options) {
    const [unscaledWidth, unscaledHeight] =
      this.pageContentFactory.getSvgElementDimensions(svgElement);

    const width = options.width || unscaledWidth;
    const height = options.height || unscaledHeight;

    return { unscaledWidth, unscaledHeight, width, height };
  }

  getPosition (svgElement, options) {
    const x = options?.position?.x ?? 0;
    const y = options?.position?.y ?? 0;
    return [x, y];
  }

  parseUrls (cssText) {
    const urls = [];

    cssText.replace(CSS_URL_REGEX, (match, quote, url) => {
      const httpsURl = url.replace('http://', 'https://');
      urls.push(httpsURl);

      return match;
    });

    return urls.filter((url) => !this.dataUrlService.isDataURL(url));
  }

  fontsAlreadyEmbedded (svgElement, fontsInUse) {
    const fontFamilies = this.fontService.parseFontFamilies(svgElement.innerHTML);

    return fontsInUse.every(({ family }) => fontFamilies.includes(family));
  }

  cssTextHasUrl (cssText) {
    return cssText.search(CSS_URL_REGEX) !== -1;
  }

  async getFontCSS (fonts) {
    const cssText = await this.fontService.getFontsCSS(fonts, {
      skipLoaded: false
    });

    if (!this.cssTextHasUrl(cssText)) return;

    const urls = this.parseUrls(cssText);
    const dataUrls = await Promise.all(urls.map((url) => this.dataUrlService.urlToDataUrl(url)));

    const embeddedCssText = dataUrls.reduce(
      (cssText, dataUrl, index) => cssText.replace(urls[index], dataUrl),
      cssText
    );

    return embeddedCssText;
  }

  async getEmbeddedFontsStyleElement (svgElement, options) {
    const fonts = this.pageContentFactory.getFonts(svgElement);

    if (this.fontsAlreadyEmbedded(svgElement, fonts)) return;

    const cssText = await this.getFontCSS(fonts);

    if (!cssText) return;

    const styleElement = document.createElement('style');
    styleElement.innerHTML = cssText;

    return styleElement;
  }

  async embedFonts (svgElement, options) {
    const styleElement = (
      options.embeddedFontsStyleElement !== null
        ? options.embeddedFontsStyleElement
        : await this.getEmbeddedFontsStyleElement(svgElement, options)
    );

    if (!styleElement) return;

    const existingDefs = svgElement.querySelector('defs');
    if (existingDefs) {
      existingDefs.appendChild(styleElement);
    } else {
      const defs = document.createElement('defs');
      defs.appendChild(styleElement);
      svgElement.insertBefore(defs, svgElement.firstChild);
    }
  }

  getImageUrlVersion (url, width, height, options) {
    if (!options.imageReplacement || options.imageReplacement === 'smart') {
      const imageVersionUrl = INKIVE.ImagesFactory.getRightSizeUrl(
        url,
        { width, height },
        true
      );

      return imageVersionUrl;
    }

    if (options.imageReplacement === 'none') {
      return url;
    }

    const version = options.imageReplacement;
    const imageVersionUrl = INKIVE.ImagesFactory.getVersionUrl(
      url,
      version,
      true
    );

    return imageVersionUrl;
  }

  async embedImage (image, options) {
    const url = image.href.baseVal;
    const width = image.width.baseVal.value * options.ratio;
    const height = image.height.baseVal.value * options.ratio;

    if (!url) throw new Error('Image URL is not defined');

    if (this.dataUrlService.isDataURL(url)) return;

    const versionUrl = this.getImageUrlVersion(url, width, height, options);

    const fallbacks = options.imageFallbacks
      .map((version) => INKIVE.ImagesFactory.getVersionUrl(url, version, true))
      .filter((url) => url !== versionUrl)
      .concat(imageFallback);

    const dataUrl = await this.dataUrlService.urlToDataUrl(versionUrl, {
      fallbacks,
      maxByteSize: options.imageMaxByteSize
    });

    image.href.baseVal = dataUrl;

    image.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', dataUrl);

    return dataUrl;
  }

  async embedImages (svgElement, options) {
    const images = this.pageContentFactory.getImages(svgElement);

    return await Promise.all(images.map((image) => this.embedImage(image, options)));
  }

  async embedResources (svgElement, options) {
    return await Promise.all([
      this.embedFonts(svgElement, options),
      this.embedImages(svgElement, options)
    ]);
  }

  svgElementToSvg (svgElement, options) {
    const { unscaledWidth, unscaledHeight, width, height } = this.getSvgElementSize(svgElement, options);
    const [x, y] = this.getPosition(svgElement, options);

    if (options.backgroundColor) {
      const backgroundColorElement = svgElement.querySelector(
        `#${SELECTOR_BACKGROUND_COLOR}`
      );
      backgroundColorElement?.setAttribute('fill', options.backgroundColor);
    }

    // Creates a wrapping svg element to prevent modifying the original one
    const svg = document.createElementNS(NS.SVG, 'svg');

    svg.setAttribute('width', `${width}`);
    svg.setAttribute('height', `${height}`);
    svg.setAttribute('viewBox', `${x} ${y} ${unscaledWidth} ${unscaledHeight}`);
    svg.setAttribute('xmlns', NS.SVG);
    svg.setAttribute('xmlns:xlink', NS.XLINK);

    svgElement.setAttribute('x', '0');
    svgElement.setAttribute('y', '0');

    svg.appendChild(svgElement);

    return svg;
  }

  async loadSvgElementAsImage (svgElement, options) {
    await this.embedResources(svgElement, options);
    const svg = await this.svgElementToSvg(svgElement, options);
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: 'image/svg+xml' });
    const blobUrl = URL.createObjectURL(blob);

    try {
      const image = await this.createImageElement(blobUrl);
      return image;
    } catch (error) {
      throw new Error('Failed to load SVG as image');
    }
  }

  getPixelRatio (ctx) {
    const backingStore = ctx?.webkitBackingStorePixelRatio;

    return (window.devicePixelRatio || 1) / (backingStore || 1);
  }

  async svgElementToCanvas (svgElement, givenOptions) {
    const options = { ...DEFAULT_OPTIONS, ...givenOptions };

    const { width, height } = this.getSvgElementSize(svgElement, options);

    const ctx = this.canvas.getContext('2d', {
      alpha: options.type === 'image/png',
      willReadFrequently: options.willReadFrequently,
      desynchronized: true
    });

    const pixelRatio = options.pixelRatio || this.getPixelRatio(ctx);
    const canvasDimensionsLimitRatio = Math.min(
      1,
      CANVAS_DIMENSION_LIMIT / width,
      CANVAS_DIMENSION_LIMIT / height
    );

    // What is the actual width?
    this.canvas.width = width * pixelRatio * canvasDimensionsLimitRatio;
    this.canvas.height = height * pixelRatio * canvasDimensionsLimitRatio;

    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    // TODO: look into fabric.js
    const svgImage = await this.loadSvgElementAsImage(svgElement, options);

    // TODO: there is a bug with Event Loop in safari when first drawing the canva
    const isSafariBrowser = isSafari() || isIOSDevice();
    if (isSafariBrowser) await new Promise(resolve => INKIVE.Helpers.afterRendering(resolve));

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(svgImage, 0, 0, this.canvas.width, this.canvas.height);

    const result = [ctx, this.canvas];

    return result;
  }

  async svgElementToImageDataUrl (svgElement, givenOptions) {
    const options = { ...DEFAULT_OPTIONS, ...givenOptions };

    const type = options.type;
    const quality = options.quality;

    const [, canvas] = await this.svgElementToCanvas(svgElement, options);
    const dataUrl = await canvas.toDataURL(type, quality);

    return dataUrl;
  }

  async svgElementToBlob (svgElement, givenOptions) {
    const options = { ...DEFAULT_OPTIONS, ...givenOptions };

    // TODO: Use a decorator for caching
    const type = options.type;
    const quality = options.quality;

    const [, canvas] = await this.svgElementToCanvas(svgElement, options);

    // getImageData doesn't output transparent pixels
    const blob = await new Promise((resolve) => (
      canvas.toBlob(
        async (blob) => resolve(blob),
        type,
        quality
      )
    ));

    return blob;
  }

  async svgElementToImageData (svgElement, givenOptions) {
    const options = { ...DEFAULT_OPTIONS, ...givenOptions };
    const [ctx, canvas] = await this.svgElementToCanvas(svgElement, options);

    const imageData = ctx.getImageData(
      0,
      0,
      canvas.width,
      canvas.height
    );

    return imageData;
  }
}
